import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, School, Building2, ChevronRight, Users, BookOpen,
  Activity, ArrowRight, Lock, Sparkles, Globe2, CheckCircle2,
  GraduationCap, FlaskConical, Zap, ArrowLeft, ExternalLink, Info, Shield,
  Search, X, Loader2
} from 'lucide-react';
import api from '../api/client';
import SEOHead from '../components/SEOHead';
import BrandLogo from '../components/BrandLogo';
import ImpactSection from '../components/ImpactSection';
import AboutNischalsSection from '../components/AboutNischalsSection';
import UniversalSearchBar from '../components/UniversalSearchBar';

const LandingPage = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [schoolType, setSchoolType] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolsTotal, setSchoolsTotal] = useState(0);
  const [schoolGroups, setSchoolGroups] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('state'); // 'state' | 'chain'
  const [chains, setChains] = useState([]);
  const [chainsLoading, setChainsLoading] = useState(false);
  const [selectedChain, setSelectedChain] = useState(null);
  const [chainBranches, setChainBranches] = useState([]);
  const [chainLoading, setChainLoading] = useState(false);

  useEffect(() => { fetchStates(); }, []);

  const fetchChainsIfNeeded = async () => {
    if (chains.length > 0 || chainsLoading) return;
    setChainsLoading(true);
    try {
      const res = await api.get('/public/school-groups');
      setChains(res.data);
    } catch (e) {
      console.error('Failed to load chains', e);
    } finally {
      setChainsLoading(false);
    }
  };

  const handleChainSelect = async (chain) => {
    setSelectedChain(chain);
    setChainBranches([]);
    setChainLoading(true);
    try {
      const res = await api.get(`/public/school-groups/${chain.id}/schools`);
      setChainBranches(res.data.schools || []);
    } catch (e) {
      console.error('Failed to load branches', e);
    } finally {
      setChainLoading(false);
    }
  };

  const handleChainHQLogin = (chain) => {
    navigate('/login', {
      state: {
        chainName: chain.group_name,
        groupAdmin: true,
      },
    });
  };

  const handleChainBranchProceed = (branch) => {
    navigate('/login', {
      state: {
        stateId: branch.state_id,
        stateName: branch.state_name,
        schoolId: branch.id,
        schoolName: branch.school_name,
        schoolUdise: branch.udise_number,
        schoolType: 'private',
        chainName: selectedChain?.group_name,
      },
    });
  };

  const handleModeToggle = (m) => {
    setMode(m);
    setStep(1);
    setSelectedState(null);
    setSelectedSchool(null);
    setSelectedChain(null);
    setChainBranches([]);
    if (m === 'chain') fetchChainsIfNeeded();
  };

  // From the universal search dropdown — jump straight to the right place.
  const handleUniversalSelect = async (kind, item) => {
    if (kind === 'school') {
      navigate('/login', {
        state: {
          stateId: item.state_id,
          stateName: item.state_name,
          schoolId: item.id,
          schoolName: item.school_name,
          schoolUdise: item.udise_number,
          schoolType: item.school_type || 'govt',
        },
      });
    } else if (kind === 'chain') {
      setMode('chain');
      setStep(1);
      fetchChainsIfNeeded();
      // Auto-open the chain
      setTimeout(() => handleChainSelect(item), 200);
      window.scrollTo({ top: document.querySelector('[aria-label="School selection wizard"]')?.offsetTop || 0, behavior: 'smooth' });
    } else if (kind === 'district') {
      // Land the user on Step 3 (school picker) already filtered by district
      const st = states.find((s) => s.id === item.state_id);
      if (st) {
        setMode('state');
        setSelectedState(st);
        const type = st.has_govt ? 'government' : 'private';
        setSelectedDistrict(item.id);
        await handleTypeSelect(type, st.id);
        // After handleTypeSelect flips to step 3, filter by the picked district
        setTimeout(() => refetchSchools({ district: item.id }), 100);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (kind === 'state') {
      const st = states.find((s) => s.id === item.id);
      if (st) handleStateSelect(st);
    }
  };

  const fetchStates = async () => {
    try {
      // Only show states that already have schools — newly added states with no
      // schools yet are hidden from the public landing page.
      const response = await api.get('/states?with_schools_only=true');
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStateSelect = async (state) => {
    setSelectedState(state);
    setSchoolType(null);
    setSelectedSchool(null);
    setSelectedGroup(null);

    if (state.has_govt && !state.has_private) {
      handleTypeSelect('government', state.id);
    } else if (!state.has_govt && state.has_private) {
      handleTypeSelect('private', state.id);
    } else if (state.has_govt && state.has_private) {
      setStep(2);
    } else {
      // no schools yet
      setSchools([]);
      setStep(3);
    }
  };

  const handleTypeSelect = async (type, stateId = selectedState?.id) => {
    setSchoolType(type);
    setSelectedSchool(null);
    setSelectedGroup(null);
    setSelectedDistrict(null);
    setSchoolSearch('');
    try {
      const [schoolsRes, districtsRes] = await Promise.all([
        api.get(`/states/${stateId}/schools?school_type=${type}&limit=50`),
        api.get(`/states/${stateId}/districts`),
      ]);
      setSchools(schoolsRes.data.results || []);
      setSchoolsTotal(schoolsRes.data.total || 0);
      setDistricts(districtsRes.data || []);
      if (type === 'private') {
        const groupsResponse = await api.get(`/states/${stateId}/school-groups`);
        setSchoolGroups(groupsResponse.data);
      }
      setStep(3);
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const refetchSchools = async ({ search = schoolSearch, group = selectedGroup, district = selectedDistrict } = {}) => {
    if (!selectedState || !schoolType) return;
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ school_type: schoolType, limit: '50' });
      if (search?.trim()) params.append('search', search.trim());
      if (group) params.append('group_id', group);
      if (district) params.append('district_id', district);
      const res = await api.get(`/states/${selectedState.id}/schools?${params.toString()}`);
      setSchools(res.data.results || []);
      setSchoolsTotal(res.data.total || 0);
    } catch (e) {
      console.error('school search failed', e);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    if (step !== 3 || !selectedState) return;
    const t = setTimeout(() => { refetchSchools({ search: schoolSearch }); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolSearch]);

  const handleGroupSelect = async (groupId) => {
    setSelectedGroup(groupId);
    setSelectedSchool(null);
    refetchSchools({ group: groupId });
  };

  const handleDistrictSelect = async (districtId) => {
    setSelectedDistrict(districtId);
    setSelectedSchool(null);
    refetchSchools({ district: districtId });
  };

  const handleProceedToLogin = () => {
    if (selectedSchool) {
      navigate('/login', {
        state: {
          stateId: selectedState.id,
          stateName: selectedState.state_name,
          stateCode: selectedState.state_code,
          themeColor: selectedState.theme_color,
          schoolId: selectedSchool.id,
          schoolName: selectedSchool.school_name,
          schoolUdise: selectedSchool.udise_number,
          schoolType,
        },
      });
    }
  };

  const handleProceedAsStateAdmin = () => {
    if (!selectedState) return;
    navigate('/login', {
      state: {
        stateId: selectedState.id,
        stateName: selectedState.state_name,
        stateCode: selectedState.state_code,
        themeColor: selectedState.theme_color,
        stateAdmin: true,
      },
    });
  };

  const handleProceedAsDistrictAdmin = (districtObj) => {
    if (!selectedState || !districtObj) return;
    navigate('/login', {
      state: {
        stateId: selectedState.id,
        stateName: selectedState.state_name,
        stateCode: selectedState.state_code,
        themeColor: selectedState.theme_color,
        districtId: districtObj.id,
        districtName: districtObj.district_name,
        districtAdmin: true,
      },
    });
  };

  const goBack = () => {
    if (step === 3) {
      if (selectedState?.has_govt && selectedState?.has_private) {
        setStep(2);
        setSchoolType(null);
      } else {
        setStep(1);
        setSelectedState(null);
      }
      setSelectedSchool(null);
      setSchools([]);
    } else if (step === 2) {
      setStep(1);
      setSelectedState(null);
      setSchoolType(null);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-rose-50">
      <SEOHead
        title="nischals EduLab Monitor — Hands-on Lab Learning for Every Classroom"
        description="A multi-state platform monitoring lab activities across thousands of schools. Pick your state, find your school, and step into your portal in seconds."
        keywords="nischals EduLab Monitor, lab monitoring, school dashboard, education platform, lab activity tracking, India education, teacher portal, government school dashboard"
      />

      {/* Decorative blurred orbs */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-indigo-300/30 blur-3xl" />
      <div className="pointer-events-none absolute top-1/4 -right-32 h-[32rem] w-[32rem] rounded-full bg-rose-200/40 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-amber-200/40 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 left-10 h-64 w-64 rounded-full bg-teal-200/30 blur-3xl" />

      {/* Header */}
      <header className="relative z-10 border-b border-slate-200/60 bg-white/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <BrandLogo variant="full" size="md" showTagline linkTo="/" />

            <div className="flex items-center gap-2 sm:gap-3">
              <a
                href="https://www.nischalsworld.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-main-site"
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                style={{ background: 'linear-gradient(135deg, #e4376f, #c11d50)', boxShadow: '0 6px 20px -8px rgba(228, 55, 111, 0.5)' }}
              >
                <Globe2 className="w-4 h-4" />
                <span className="hidden sm:inline">Visit nischalsworld.com</span>
                <span className="sm:hidden">nischalsworld</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-80" />
              </a>
              <button
                onClick={() => navigate('/master-login')}
                data-testid="header-master-login"
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl bg-white hover:bg-slate-50 text-slate-700 hover:text-indigo-700 text-sm font-semibold border border-slate-200 hover:border-indigo-200 hover:shadow-md hover:shadow-indigo-100 transition-all"
              >
                <Lock className="w-4 h-4" />
                <span className="hidden md:inline">Master Login</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Floating decorative blobs around the hero */}
        <div className="pointer-events-none absolute top-20 left-1/4 w-6 h-6 rounded-full bg-amber-400/60 blur-md animate-pulse" style={{ animationDuration: '3s' }} />
        <div className="pointer-events-none absolute top-40 right-1/3 w-4 h-4 rounded-full bg-fuchsia-400/60 blur-md animate-pulse" style={{ animationDuration: '4s', animationDelay: '1s' }} />
        <div className="pointer-events-none absolute top-60 left-12 w-5 h-5 rounded-full bg-teal-400/60 blur-md animate-pulse" style={{ animationDuration: '5s', animationDelay: '0.5s' }} />

        {/* UNIVERSAL SEARCH — appears above the hero so anyone can jump straight to their school */}
        <section className="mb-10 sm:mb-14" aria-label="Universal search">
          <div className="text-center mb-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Quick access</p>
            <h3 className="text-lg sm:text-xl font-semibold text-slate-800">
              Know your school? <span className="text-indigo-700">Jump right in.</span>
            </h3>
          </div>
          <UniversalSearchBar onSelect={handleUniversalSelect} />
        </section>

        {/* HERO — Welcome message + selection wizard side-by-side */}
        <section className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-20 sm:mb-24" aria-label="Welcome">
          {/* Left: welcome message */}
          <div className="lg:col-span-5 lg:pt-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-indigo-200 shadow-sm shadow-indigo-100 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              <span className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider">Welcome to</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-slate-900 tracking-tight leading-[1.05] mb-5">
              <span className="bg-gradient-to-r from-rose-600 via-fuchsia-600 to-indigo-600 bg-clip-text text-transparent" style={{ color: '#e4376f' }}>nischals</span>{' '}
              <span className="text-slate-900">EduLab</span>
              <span className="block mt-1.5 bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-amber-500 bg-clip-text text-transparent">Monitor</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-md">
              India's first <strong className="text-slate-900">multi-state lab learning platform</strong>. Track every chapter, every activity, every classroom — across <strong className="text-slate-900">8,000+ schools</strong> and <strong className="text-slate-900">10+ countries</strong>.
            </p>

            {/* Quick "what is this" floating chips */}
            <div className="flex flex-wrap gap-2 mb-6">
              <FloatingChip icon={GraduationCap} label="For Teachers" gradient="from-teal-100 to-emerald-100" iconColor="text-teal-700" />
              <FloatingChip icon={Building2} label="For Schools" gradient="from-indigo-100 to-violet-100" iconColor="text-indigo-700" />
              <FloatingChip icon={MapPin} label="For Districts &amp; States" gradient="from-fuchsia-100 to-pink-100" iconColor="text-fuchsia-700" />
            </div>

            {/* Hint arrow toward wizard */}
            <div className="hidden lg:flex items-center gap-2 text-indigo-600 font-semibold text-sm animate-pulse" style={{ animationDuration: '2.5s' }}>
              <ArrowRight className="w-4 h-4" />
              <span>Get started in 3 quick steps</span>
            </div>
            <div className="lg:hidden text-center mt-2 text-sm font-semibold text-indigo-600">Pick your state below to begin ↓</div>
          </div>

          {/* Right: selection wizard with rich floating accents */}
          <div className="lg:col-span-7" aria-label="School selection wizard">
            <div className="relative">
              {/* Layered glow halos behind the card */}
              <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-amber-300/30 blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '4s' }} />
              <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-fuchsia-400/30 blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '5s', animationDelay: '1s' }} />
              <div className="absolute top-1/3 -left-10 w-24 h-24 rounded-full bg-indigo-400/20 blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '6s' }} />
              <div className="absolute -inset-1.5 bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-amber-400 rounded-3xl blur-2xl opacity-40 pointer-events-none" />

              {/* Floating "Get Started" badge */}
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white text-[11px] font-bold uppercase tracking-wider shadow-lg shadow-fuchsia-300/50">
                <Sparkles className="w-3.5 h-3.5" />
                Get Started
              </div>

              <div className="relative bg-white rounded-3xl border border-slate-200/80 shadow-2xl shadow-indigo-300/30 overflow-hidden">
                {/* Top gradient strip */}
                <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-amber-400" />

                {/* Header */}
                <div className="px-6 sm:px-7 pt-7 pb-3">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Find your school</h2>
                  <p className="text-sm text-slate-500 mt-1">Pick your state → category → school to step into your portal.</p>

                  {/* Mode toggle: By State or By Chain */}
                  <div className="mt-4 inline-flex p-1 bg-slate-100 rounded-xl gap-1" role="tablist">
                    <button
                      type="button"
                      onClick={() => handleModeToggle('state')}
                      data-testid="mode-state"
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === 'state' ? 'bg-white text-indigo-700 shadow' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      <MapPin className="w-3.5 h-3.5 inline mr-1" /> By State
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeToggle('chain')}
                      data-testid="mode-chain"
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === 'chain' ? 'bg-white text-fuchsia-700 shadow' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                      <Globe2 className="w-3.5 h-3.5 inline mr-1" /> School Chain
                    </button>
                  </div>
                </div>

                {/* Progress */}
                <div className="px-6 sm:px-7 py-4 border-y border-slate-100 bg-gradient-to-r from-indigo-50/60 to-fuchsia-50/60">
                  <StepProgress step={step} hasTypeStep={selectedState ? (selectedState.has_govt && selectedState.has_private) : true} />
                </div>

                <div className="p-6 sm:p-7">
                  {mode === 'chain' ? (
                    <Step1Chains
                      loading={chainsLoading}
                      chains={chains}
                      selectedChain={selectedChain}
                      onChainSelect={handleChainSelect}
                      onChainBack={() => setSelectedChain(null)}
                      branches={chainBranches}
                      branchesLoading={chainLoading}
                      onChainHQLogin={handleChainHQLogin}
                      onBranchSelect={handleChainBranchProceed}
                    />
                  ) : (
                    <>
                      {step === 1 && (
                        <Step1States loading={loading} states={states} onSelect={handleStateSelect} />
                      )}
                      {step === 2 && selectedState && (
                        <Step2Type
                          state={selectedState}
                          onBack={goBack}
                          onSelect={(t) => handleTypeSelect(t)}
                          onStateAdmin={handleProceedAsStateAdmin}
                        />
                      )}
                      {step === 3 && (
                        <Step3School
                          state={selectedState}
                          schoolType={schoolType}
                          schools={schools}
                          schoolsTotal={schoolsTotal}
                          selectedSchool={selectedSchool}
                          setSelectedSchool={setSelectedSchool}
                          schoolGroups={schoolGroups}
                          selectedGroup={selectedGroup}
                          handleGroupSelect={handleGroupSelect}
                          districts={districts}
                          selectedDistrict={selectedDistrict}
                          handleDistrictSelect={handleDistrictSelect}
                          schoolSearch={schoolSearch}
                          setSchoolSearch={setSchoolSearch}
                          searchLoading={searchLoading}
                          onBack={goBack}
                          onProceed={handleProceedToLogin}
                          onStateAdmin={handleProceedAsStateAdmin}
                          onDistrictAdmin={handleProceedAsDistrictAdmin}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats — nischals' overall impact across the world */}
        <ImpactSection />

        {/* About nischals — for new visitors */}
        <AboutNischalsSection />

        {/* Features */}
        <section className="mt-20" aria-label="Features">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center tracking-tight mb-3">Built for modern classrooms</h3>
          <p className="text-center text-slate-500 mb-10">Everything teachers, admins, and policy makers need in one place.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Zap}
              title="Track Activities"
              desc="Capture photos and videos of every lab session. See chapter coverage and engagement at a glance."
              gradient="from-indigo-500 to-violet-500"
              glow="shadow-indigo-300/40"
            />
            <FeatureCard
              icon={GraduationCap}
              title="Empower Teachers"
              desc="Built-in activity planner mapped to official curriculum. Teachers spend less time reporting, more time teaching."
              gradient="from-teal-500 to-emerald-500"
              glow="shadow-teal-300/40"
            />
            <FeatureCard
              icon={Sparkles}
              title="Data-Driven Insights"
              desc="Real-time analytics on school performance, usage patterns, and districts needing intervention."
              gradient="from-fuchsia-500 to-pink-500"
              glow="shadow-fuchsia-300/40"
            />
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-200/60 bg-white/60 backdrop-blur-xl mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>© 2026 nischals EduLab Monitor · Joy of Knowing™</p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.nischalsworld.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-rose-600 transition-colors font-medium"
              style={{ color: '#e4376f' }}
            >
              nischalsworld.com
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <span>A nischals Technology product</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ------------------------- SUB COMPONENTS ------------------------- */
const HeroStat = ({ icon: Icon, value, label, gradient, glow }) => (
  <div className="group relative">
    <div className={`absolute -inset-0.5 bg-gradient-to-r ${gradient} rounded-2xl blur opacity-25 group-hover:opacity-50 transition-opacity`} />
    <div className="relative bg-white rounded-2xl p-5 border border-slate-200/80 shadow-lg shadow-slate-200/40 hover:-translate-y-1 transition-all text-center">
      <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} items-center justify-center shadow-lg ${glow} mb-2`}>
        <Icon className="w-5 h-5 text-white" strokeWidth={2.4} />
      </div>
      <div className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  </div>
);

const FloatingChip = ({ icon: Icon, label, gradient, iconColor }) => (
  <div className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${gradient} border border-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}>
    <Icon className={`w-3.5 h-3.5 ${iconColor}`} strokeWidth={2.5} />
    <span className="text-[11px] font-bold text-slate-800" dangerouslySetInnerHTML={{ __html: label }} />
  </div>
);


const FeatureCard = ({ icon: Icon, title, desc, gradient, glow }) => (
  <div className="group relative">
    <div className={`absolute -inset-0.5 bg-gradient-to-r ${gradient} rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity`} />
    <article className="relative bg-white rounded-2xl p-7 border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:-translate-y-1 transition-all">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 shadow-lg ${glow}`}>
        <Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
      </div>
      <h4 className="text-lg font-bold text-slate-900 mb-2">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </article>
  </div>
);

const StepProgress = ({ step, hasTypeStep }) => {
  const steps = hasTypeStep
    ? [{ n: 1, label: 'Select State' }, { n: 2, label: 'School Type' }, { n: 3, label: 'Select School' }]
    : [{ n: 1, label: 'Select State' }, { n: 3, label: 'Select School' }];

  return (
    <ol className="flex items-center justify-between">
      {steps.map((s, idx) => {
        const isDone = step > s.n;
        const isActive = step === s.n;
        return (
          <React.Fragment key={s.n}>
            <li className={`flex items-center gap-2 ${isActive || isDone ? 'text-indigo-700' : 'text-slate-400'}`}>
              <div className={`relative w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white shadow-lg shadow-indigo-300/60'
                  : isDone
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-300/60'
                  : 'bg-slate-200 text-slate-500'
              }`}>
                {isDone ? <CheckCircle2 className="w-5 h-5" /> : steps.findIndex(x => x.n === s.n) + 1}
              </div>
              <span className="text-xs font-semibold hidden sm:block">{s.label}</span>
            </li>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-3 rounded-full ${step > s.n ? 'bg-emerald-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
};

const Step1Chains = ({ loading, chains, selectedChain, onChainSelect, onChainBack, branches, branchesLoading, onChainHQLogin, onBranchSelect }) => {
  if (selectedChain) {
    return (
      <div>
        <button onClick={onChainBack} data-testid="chain-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-fuchsia-600 mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to chains
        </button>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900">{selectedChain.group_name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {selectedChain.is_national ? 'National chain' : selectedChain.state_name} · {selectedChain.school_count} branches
            </p>
          </div>
          <button
            onClick={() => onChainHQLogin(selectedChain)}
            data-testid="chain-hq-login"
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white text-xs font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
          >
            <Shield className="w-3.5 h-3.5" /> Login as Chain HQ
          </button>
        </div>
        {branchesLoading ? (
          <div className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin" /></div>
        ) : branches.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">No branches yet under this chain.</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {branches.map((b) => (
              <button
                key={b.id}
                onClick={() => onBranchSelect(b)}
                data-testid={`chain-branch-${b.id}`}
                className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-white border border-slate-200 hover:border-fuchsia-300 hover:shadow-md hover:shadow-fuchsia-100 hover:-translate-y-0.5 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center text-white flex-shrink-0">
                  <School className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{b.school_name}</div>
                  <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {b.state_name}{b.district_name ? ` · ${b.district_name}` : ''}{b.city ? ` · ${b.city}` : ''}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-xl font-bold text-slate-900 mb-1">Select Your School Chain</h3>
      <p className="text-sm text-slate-500 mb-5">National & multi-state chains using nischals EduLab.</p>
      {loading ? (
        <div className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin" /></div>
      ) : chains.length === 0 ? (
        <div className="text-center py-10 text-slate-400 text-sm">
          No school chains onboarded yet.<br/>
          <span className="text-xs">Ask your Master Admin to add your chain under <strong>School Groups</strong>.</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {chains.map((c) => (
            <button
              key={c.id}
              onClick={() => onChainSelect(c)}
              data-testid={`chain-card-${c.id}`}
              className="group flex items-center gap-4 p-4 rounded-2xl bg-white border-2 border-slate-200 hover:border-fuchsia-300 hover:shadow-lg hover:shadow-fuchsia-100 hover:-translate-y-0.5 transition-all text-left"
            >
              <div className="relative w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white shadow-lg shadow-fuchsia-300/60 flex-shrink-0">
                <Globe2 className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 group-hover:text-fuchsia-700 transition-colors truncate flex items-center gap-2">
                  {c.group_name}
                  {c.is_national && (
                    <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded uppercase tracking-wider">National</span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {c.school_count} branches{c.state_spread > 1 ? ` · ${c.state_spread} states` : c.state_spread === 1 ? ` · ${c.state_name}` : ''}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-fuchsia-500 group-hover:translate-x-0.5 transition-all" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const Step1States = ({ loading, states, onSelect }) => (
  <div>
    <h3 className="text-xl font-bold text-slate-900 mb-1">Select Your State</h3>
    <p className="text-sm text-slate-500 mb-5">Where is your school located?</p>
    {loading ? (
      <div className="text-center py-10 text-slate-400">Loading states...</div>
    ) : states.length === 0 ? (
      <div className="text-center py-10 text-slate-400">No states available yet.</div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {states.map((state) => (
          <button
            key={state.id}
            onClick={() => onSelect(state)}
            data-testid={`state-card-${state.state_code}`}
            className="group flex items-center gap-4 p-4 rounded-2xl bg-white border-2 border-slate-200 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100 hover:-translate-y-0.5 transition-all text-left"
          >
            <div
              className="relative w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg flex-shrink-0"
              style={{ backgroundColor: state.theme_color || '#6366f1' }}
            >
              <span className="relative">{state.state_code}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 group-hover:text-indigo-700 transition-colors truncate">
                {state.state_name}
              </div>
              <div className="text-xs text-slate-500">
                {state.total_schools.toLocaleString()} schools
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
          </button>
        ))}
      </div>
    )}
  </div>
);

const Step2Type = ({ state, onBack, onSelect, onStateAdmin }) => (
  <div>
    <button onClick={onBack} data-testid="back-btn" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 mb-4 transition-colors">
      <ArrowLeft className="w-4 h-4" /> Back to states
    </button>
    <h3 className="text-xl font-bold text-slate-900 mb-1">{state.state_name}</h3>
    <p className="text-sm text-slate-500 mb-5">Choose your school category</p>

    {/* Premium State Admin block — placed at the top, for state-level officials */}
    <PremiumStateAdminCTA state={state} onClick={onStateAdmin} />

    {/* Divider */}
    <div className="flex items-center gap-3 my-5">
      <div className="h-px flex-1 bg-slate-200" />
      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">or browse schools</span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {state.has_govt && (
        <button
          onClick={() => onSelect('government')}
          data-testid="type-government"
          className="group relative flex flex-col items-center gap-3 p-6 rounded-2xl bg-white border-2 border-slate-200 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100 hover:-translate-y-1 transition-all"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl blur-md opacity-50 group-hover:opacity-80 transition-opacity" />
            <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-300/60">
              <Building2 className="w-8 h-8 text-white" strokeWidth={2.2} />
            </div>
          </div>
          <div className="text-center">
            <div className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">Government Schools</div>
            <div className="text-xs text-slate-500 mt-0.5">{state.govt_schools.toLocaleString()} schools</div>
          </div>
        </button>
      )}
      {state.has_private && (
        <button
          onClick={() => onSelect('private')}
          data-testid="type-private"
          className="group relative flex flex-col items-center gap-3 p-6 rounded-2xl bg-white border-2 border-slate-200 hover:border-fuchsia-300 hover:shadow-lg hover:shadow-fuchsia-100 hover:-translate-y-1 transition-all"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500 to-pink-600 rounded-2xl blur-md opacity-50 group-hover:opacity-80 transition-opacity" />
            <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center shadow-lg shadow-fuchsia-300/60">
              <School className="w-8 h-8 text-white" strokeWidth={2.2} />
            </div>
          </div>
          <div className="text-center">
            <div className="font-bold text-slate-900 group-hover:text-fuchsia-700 transition-colors">Private Schools</div>
            <div className="text-xs text-slate-500 mt-0.5">{state.private_schools.toLocaleString()} schools</div>
          </div>
        </button>
      )}
    </div>
  </div>
);

const fieldCls = "w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-100 transition-all";

const Step3School = ({
  state, schoolType, schools, schoolsTotal, selectedSchool, setSelectedSchool,
  schoolGroups, selectedGroup, handleGroupSelect,
  districts, selectedDistrict, handleDistrictSelect,
  schoolSearch, setSchoolSearch, searchLoading,
  onBack, onProceed, onStateAdmin, onDistrictAdmin,
}) => (
  <div>
    <button onClick={onBack} data-testid="back-btn" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 mb-4 transition-colors">
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
    <h3 className="text-xl font-bold text-slate-900 mb-1">{state?.state_name}</h3>
    <p className="text-sm text-slate-500 mb-5">{schoolType === 'government' ? 'Government Schools' : 'Private Schools'} · {schoolsTotal.toLocaleString()} total</p>

    {/* Premium State Admin block — top placement for state-level officials.
        Hidden once a specific school is selected (those users should sign in as that school's super admin / teacher). */}
    {!selectedSchool && (
      <PremiumStateAdminCTA state={state} onClick={onStateAdmin} compact />
    )}

    {/* Divider */}
    {!selectedSchool && (
      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">or pick a school</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
    )}

    {/* Filter row: district + group */}
    <div className={`grid ${schoolType === 'private' && schoolGroups.length > 0 ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mb-3`}>
      {districts.length > 1 && (
        <div>
          <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">District</label>
          <select
            value={selectedDistrict || ''}
            onChange={(e) => handleDistrictSelect(e.target.value ? parseInt(e.target.value) : null)}
            data-testid="district-filter"
            className={fieldCls}
          >
            <option value="">All Districts</option>
            {districts.map((d) => (
              <option key={d.id} value={d.id}>{d.district_name} ({d.school_count})</option>
            ))}
          </select>
        </div>
      )}
      {schoolType === 'private' && schoolGroups.length > 0 && (
        <div>
          <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">School Group</label>
          <select
            value={selectedGroup || ''}
            onChange={(e) => handleGroupSelect(e.target.value ? parseInt(e.target.value) : null)}
            data-testid="group-select"
            className={fieldCls}
          >
            <option value="">All Groups</option>
            {schoolGroups.map((g) => (
              <option key={g.id} value={g.id}>{g.group_name} ({g.school_count})</option>
            ))}
          </select>
        </div>
      )}
    </div>

    {/* Inline District Admin CTA — appears the moment a district is picked (govt only).
        Hidden once a specific school is selected. */}
    {schoolType === 'government' && selectedDistrict && !selectedSchool && (() => {
      const d = districts.find((x) => x.id === selectedDistrict);
      if (!d) return null;
      return (
        <button
          onClick={() => onDistrictAdmin(d)}
          data-testid="district-admin-login-btn"
          className="group w-full flex items-center gap-2.5 mb-3 px-3.5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-50 via-white to-teal-50 border border-emerald-200 hover:border-emerald-400 hover:shadow-md hover:shadow-emerald-100 hover:-translate-y-0.5 transition-all text-left"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm shadow-emerald-300/50 flex-shrink-0">
            <Shield className="w-4 h-4 text-white" strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-900 truncate">
              Sign in as <span className="text-emerald-700">{d.district_name}</span> District Admin
            </p>
            <p className="text-[10px] text-slate-500">{d.school_count} government schools · {state?.state_name}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
        </button>
      );
    })()}

    {/* Search input */}
    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Find your school</label>
    <div className="relative mb-3">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type="text"
        value={schoolSearch}
        onChange={(e) => setSchoolSearch(e.target.value)}
        placeholder="Search by name, UDISE or district"
        data-testid="school-search-input"
        className="w-full pl-10 pr-10 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-100 transition-all"
      />
      {schoolSearch && (
        <button
          type="button"
          onClick={() => setSchoolSearch('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      {searchLoading && (
        <Loader2 className="absolute right-9 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 animate-spin" />
      )}
    </div>

    {/* Results list (capped at 50 — server returns top 50 matches) */}
    <div className="mb-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="school-results-list">
        {schools.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">
            {searchLoading ? 'Searching…' : 'No schools match your search.'}
          </div>
        ) : (
          schools.map((school) => {
            const isActive = selectedSchool?.id === school.id;
            return (
              <button
                key={school.id}
                type="button"
                onClick={() => setSelectedSchool(school)}
                data-testid={`school-row-${school.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isActive ? 'bg-gradient-to-r from-indigo-50 to-fuchsia-50' : 'hover:bg-slate-50'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isActive ? 'bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500'
                }`}>
                  <School className="w-4.5 h-4.5" strokeWidth={2.2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold truncate ${isActive ? 'text-indigo-700' : 'text-slate-900'}`}>{school.school_name}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className="font-mono">{school.udise_number}</span>
                    {school.district && (<><span>·</span><span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{school.district}</span></>)}
                  </p>
                </div>
                {isActive && <CheckCircle2 className="w-4 h-4 text-indigo-500 flex-shrink-0" />}
              </button>
            );
          })
        )}
      </div>
      {schoolsTotal > schools.length && (
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
          Showing top {schools.length} of {schoolsTotal.toLocaleString()} — refine your search to narrow results.
        </div>
      )}
    </div>

    <button
      onClick={onProceed}
      disabled={!selectedSchool}
      data-testid="proceed-login-btn"
      className="relative group w-full py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-indigo-300/60 hover:shadow-xl hover:shadow-indigo-400/60 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all overflow-hidden"
    >
      Proceed to Login
      <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
    </button>
  </div>
);

/* PREMIUM STATE ADMIN CTA — designed for bureaucrats / ministers / DEOs.
   Light pearl-ivory background with deep indigo lettering and gold-emboss insignia.
   Refined, ministerial, and harmonises with the rest of the light theme. */
const PremiumStateAdminCTA = ({ state, onClick, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="state-admin-login-btn"
    className={`group relative w-full overflow-hidden rounded-2xl text-left transition-all hover:-translate-y-0.5 ${compact ? 'p-3.5' : 'p-4 sm:p-5'}`}
    style={{
      background:
        'linear-gradient(135deg, #fffdf7 0%, #fff8e6 35%, #faf6ec 70%, #f1ece0 100%)',
      boxShadow:
        '0 14px 30px -12px rgba(180,131,30,0.25), 0 0 0 1px rgba(217,172,79,0.22) inset',
    }}
  >
    {/* Soft gold halos */}
    <span className="pointer-events-none absolute -top-10 -right-10 w-36 h-36 rounded-full bg-amber-200/40 blur-3xl" />
    <span className="pointer-events-none absolute -bottom-10 -left-12 w-32 h-32 rounded-full bg-indigo-200/30 blur-3xl" />
    {/* Double border ring — gold inside, indigo outside */}
    <span className="pointer-events-none absolute inset-1 rounded-xl border border-amber-300/40" />
    {/* shimmer */}
    <span
      className="pointer-events-none absolute -inset-y-1 -left-1/2 w-1/3 bg-gradient-to-r from-transparent via-amber-200/40 to-transparent rotate-12 opacity-0 group-hover:opacity-100 group-hover:translate-x-[400%] transition-all duration-700"
      aria-hidden
    />

    <div className="relative flex items-center gap-3 sm:gap-4">
      {/* Insignia */}
      <div className="relative flex-shrink-0">
        <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-amber-300/60 to-amber-500/30 blur-md" />
        <div
          className={`relative ${compact ? 'w-11 h-11' : 'w-14 h-14'} rounded-2xl flex items-center justify-center shadow-lg shadow-amber-300/40`}
          style={{
            background:
              'linear-gradient(135deg, #fef3c7 0%, #fbbf24 45%, #d97706 100%)',
          }}
        >
          <Shield className={`${compact ? 'w-5 h-5' : 'w-7 h-7'} text-indigo-950`} strokeWidth={2.6} />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700 mb-0.5">
          Government Portal · State Admin
        </p>
        <p className={`font-bold truncate ${compact ? 'text-sm' : 'text-base sm:text-lg'} text-indigo-950`}>
          Sign in for {state?.state_name || 'your state'}
        </p>
        {!compact && (
          <p className="text-[11px] sm:text-xs text-slate-600 mt-1 leading-snug">
            Reserved for state-level officials — Department of Education, Secretariat &amp; bureau access.
          </p>
        )}
      </div>

      <div className={`flex-shrink-0 inline-flex items-center gap-1 rounded-full border transition-colors ${compact ? 'px-2.5 py-1' : 'px-3 py-1.5'} bg-white/70 border-amber-300/60 text-indigo-900 group-hover:bg-indigo-900 group-hover:border-indigo-900 group-hover:text-amber-200`}>
        <span className={`font-bold uppercase tracking-wider ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
          Enter
        </span>
        <ChevronRight className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} group-hover:translate-x-0.5 transition-all`} />
      </div>
    </div>
  </button>
);

export default LandingPage;
